import Component from '@glimmer/component';

import { Input } from '@ember/component';
import { array, get } from '@ember/helper';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { linkToAction } from 'ember-route-state/decorators/link-to-action.js';

import CheckAllButton from 'rise-ui/components/check-all-button.gjs';
import MultiselectGroupedCheckboxes from 'rise-ui/components/multiselect-grouped-checkboxes.gjs';
import QuickFilter from 'rise-ui/components/quick-filter.gjs';
import SearchButton from 'rise-ui/components/search-button.gjs';
import classMod from 'rise-ui/helpers/class-mod.js';
import classModConfig from 'rise-ui/helpers/class-mod-config.js';
import didInsertUpdate from 'rise-ui/modifiers/did-insert-update.js';
import escapeRegexp from 'rise-ui/utils/escape-regexp.js';
import { eq, not, or } from 'rise-ui/utils/object/computed.js';
import { findCommonWords } from 'rise-ui/utils/string.js';
import { fromPx } from 'rise-ui/utils/style.js';

@linkToAction
export default class FilterGroupComponent extends Component {
  minExpandedOptions = 1;
  maxOptionsLines = 2;

  @tracked expandedOptionCount = 0;
  @tracked quickFilter = '';
  @tracked hasQuickFilter = false;

  defaultLabelProperty = 'label';
  @or('args.{labelProperty,config.labelProperty}', 'defaultLabelProperty') labelProperty;
  defaultValueProperty = 'value';
  @or('args.{valueProperty,config.valueProperty}', 'defaultValueProperty') valueProperty;

  expandedOptions = new Set();
  get hasExpandedOptions() {
    return this.expandedOptionCount >= this.minExpandedOptions;
  }
  defaultOptions = [];
  @or('args.{options,config.options}', 'defaultOptions') options;

  defaultOptionSelections = [];
  @or('args.{selection,config.selection}', 'defaultOptionSelections') optionSelections;
  // Whether no options are actually selected
  @not('optionSelections.length') isNoneChecked;
  // Whether all options are actually selected
  @eq('options.length', 'optionSelections.length') isAllChecked;

  defaultLabelKey = 'label';
  // Key for filter option label in @option.@each
  @or('labelProperty', 'defaultLabelKey') labelKey;
  defaultValueKey = 'value';
  // Key for filter option value in @option.@each
  @or('valueProperty', 'defaultValueKey') valueKey;
  // Attributes @action, @onSelectionChange, @config.action, @config.onSelectionChange
  @or('args.{action,onSelectionChange,config.action,config.onSelectionChange}')
  selectionChangeHandler;
  get filteredOptions() {
    const { labelKey, valueKey, quickFilter, options } = this; // Consume
    return options.filter(o => {
      if (!o) {
        return false;
      }
      const filter = new RegExp(escapeRegexp(quickFilter), 'i');
      return filter.test(o[labelKey]) || filter.test(o[valueKey]);
    });
  }

  get groupedOptions() {
    const { filteredOptions, labelKey } = this; // Consume
    const minOccurrence = 3;
    const optionGroups = new Map();

    if (this.args.groups) {
      findCommonWords(filteredOptions.map(o => o[labelKey]), {
        minOccurrence,
      }).forEach(s => {
        s.source = s.source.filter(i => {
          const isUngrouped = !optionGroups.has(i);
          if (isUngrouped) {
            optionGroups.set(i, s);
          }
          return isUngrouped;
        });
      });
    }

    // Group grouped and non-grouped options
    const groups = [];
    const groupedOptions = new Set();
    filteredOptions.forEach((option, i) => {
      if (groupedOptions.has(i)) {
        return;
      }
      const lastGroup = groups[groups.length - 1];
      let group = optionGroups.get(i) || {};
      if (lastGroup && lastGroup.parts === group.parts) {
        lastGroup.options.push(option);
        groupedOptions.add(i);
      } else {
        groups.push({
          parts: group.parts,
          name: group.parts && group.parts.slice(0, -1).join(''),
          options: (group.source || [ i ]).map(i => {
            groupedOptions.add(i);
            return filteredOptions[i];
          }),
        });
      }
    });

    return groups;
  }

  // On selection change: callback with selection
  @action onChange(selection, ...args) {
    return this.callLinkToActionByName('selectionChangeHandler', selection, ...args);
  }

  // On "Check All" button: check/uncheck all, then callback with selection
  @action checkAll(check) {
    this.onChange(check ? this.options.map(o => o.value) : []);
  }

  @action onQuickFilter(text) {
    this.hasFiltered = true;
    return this.quickFilter = text || '';
  }

  @action setLabelSize(el, [ index, label ]) {
    if (this.hasFiltered) {
      return;
    }
    const height = el.clientHeight;
    const style = getComputedStyle(el);
    const lineHeight = fromPx(style.getPropertyValue('line-height'));
    const lines = height / Math.floor(lineHeight);
    if (lines > this.maxOptionsLines) {
      this.expandedOptions.add(index);
    } else {
      this.expandedOptions.delete(index);
    }
    this.expandedOptionCount = this.expandedOptions.size;
  }

  @action onResize(isOverflowing, containerHeight, contentHeight, hasGroups) {
    if (this.hasFiltered) {
      return;
    }
    this.hasQuickFilter = hasGroups || contentHeight - containerHeight > 100;
  }

  <template>
    {{#let (classModConfig "c-filter-group") as | classModCfg |}}
    <div class={{classMod config=classModCfg}}>
      <div class={{classMod config=classModCfg suffix="__head"}}>
        <CheckAllButton
          @classMod={{array true "--filter"}}
          @action={{this.checkAll}}
          @isNoneChecked={{this.isNoneChecked}}
          @isAllChecked={{this.isAllChecked}}
        />
        <span class={{classMod config=classModCfg suffix="__title"}}
        >{{@label}}</span>
        <SearchButton
          class="c-search-button--filter"
          @route={{@route}}
          @searchLabel="Filter"
          @loading={{@loading}}
          @refresh={{@refresh}}
        />
      </div>
      {{#if this.hasQuickFilter}}
        <div><QuickFilter @action={{this.onQuickFilter}} /></div>
      {{/if}}
      <MultiselectGroupedCheckboxes
        class={{classMod config=classModCfg suffix="__items"}}
        @modifiers={{if this.hasExpandedOptions "large"}}
        @valueProperty={{this.valueKey}}
        @options={{this.groupedOptions}}
        @selection={{this.optionSelections}}
        @onchange={{this.onChange}}
        @updateSelectionValue={{false}}
        @onResize={{this.onResize}}
        as | option isSelected index |
      >
        <li>
          {{#let (get option this.labelKey) as | labelText |}}
          <label {{didInsertUpdate this.setLabelSize index labelText}}>
            <Input @type="checkbox" @checked={{isSelected}} />
            {{labelText}}
          </label>
          {{/let}}
        </li>
      </MultiselectGroupedCheckboxes>
    </div>
    {{/let}}
  </template>
}
